// WidthPresetPreference — Modern 太さプリセット (§9.11、consult 2026-09-29) の
// 登録リストを per-user で localStorage に永続化する infrastructure 層ユーティリティ。
//
// 背景 (docs/_wip/modern-layout.md §9.11):
//   - Modern は **明示登録型** (Procreate/MediBang 型、決定1)。ボタンはクリックで
//     太さを適用するだけで、スライダ操作では登録値を変えない。「+」で現在値を登録、
//     既定 3 個・最大 5 個 (§9.11 細目)。
//   - 値のバリデーション/正規化 (clamp・重複除去・昇順・上限個数) は presentation 層の
//     純ロジック `widthPresets.ts` を再利用する (`modernWorkspacePreference.ts` が
//     `workspaceLayout.ts` の純ロジックを再利用するのと同じ分担、依存方向も同じ:
//     infrastructure → presentation の純関数 import は本 repo の既存パターン)。
//   - Classic の `penWidths` (6 cell 上書き型) は in-memory のまま。永続化キーも別
//     (§9.11 決定4、Classic 据え置き)。
//
// 設計は `aaPreference.ts` / `modernWorkspacePreference.ts` と同流儀 (StorageLike +
// sentinel probe + storage 不可時 no-op、壊れた値は default にフォールバック)。
//
// Tests: widthPresetPreference.test.ts (storage-injection style)。

import {
  DEFAULT_WIDTH_PRESETS,
  isFiniteNumberArray,
  normalizeWidthPresets,
} from '../presentation/layouts/modern/widthPresets.js';

/** localStorage キー: Modern 太さプリセット。Classic `penWidths` とは別軸・別キー。 */
export const WIDTH_PRESETS_KEY = 'oekaki:width-presets';

/**
 * Minimal Storage shape this module uses (aaPreference.ts / modernWorkspacePreference.ts
 * と同流儀). `globalThis.localStorage` matches; tests pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface WidthPresetPreferenceStore {
  /**
   * 現在の登録リストを読み出し (正規化済み・昇順). 不在 / 不正 (JSON parse 失敗・
   * 非数値配列・正規化後に空) / storage 不可 → `DEFAULT_WIDTH_PRESETS`.
   */
  get(): readonly number[];
  /** 登録リストを保存 (JSON、書き込み前に正規化する). storage 不可なら no-op. */
  set(presets: readonly number[]): void;
  /** key を削除 (= 既定に戻す). テスト / 設定リセット用. storage 不可なら no-op. */
  clear(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`.
 * aaPreference.ts / modernWorkspacePreference.ts の probe と同じ (sentinel write +
 * readback で Safari private browsing / QuotaExceeded を弾く).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:width-presets__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateWidthPresetPreferenceStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op path
   * (= getter always returns DEFAULT_WIDTH_PRESETS, setter silently drops).
   *
   * テストでは小さな `Map` ベースの fake を渡す.
   */
  storage?: StorageLike | null;
}

const readPresets = (storage: StorageLike): readonly number[] => {
  try {
    const raw = storage.getItem(WIDTH_PRESETS_KEY);
    if (raw === null) return DEFAULT_WIDTH_PRESETS;
    const parsed: unknown = JSON.parse(raw);
    if (!isFiniteNumberArray(parsed)) return DEFAULT_WIDTH_PRESETS;
    const normalized = normalizeWidthPresets(parsed);
    // 正規化後に空 (= 保存データが全部 clamp 対象外だった等) は壊れた値扱いで default。
    return normalized.length === 0 ? DEFAULT_WIDTH_PRESETS : normalized;
  } catch {
    // JSON parse 失敗 / SecurityError 等 — default に倒す (例外で UI を壊さない).
    return DEFAULT_WIDTH_PRESETS;
  }
};

const writePresets = (storage: StorageLike, presets: readonly number[]): void => {
  try {
    storage.setItem(WIDTH_PRESETS_KEY, JSON.stringify(normalizeWidthPresets(presets)));
  } catch {
    // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる.
  }
};

/**
 * `WidthPresetPreferenceStore` のインスタンスを構築. storage が unavailable な環境
 * (SSR / Safari private browsing 厳格モード) でも getter は default を返すだけで、
 * setter は no-op (= 例外で UI が壊れることはない).
 */
export const createWidthPresetPreferenceStore = (
  opts: CreateWidthPresetPreferenceStoreOptions = {},
): WidthPresetPreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    get() {
      if (storage === null) return DEFAULT_WIDTH_PRESETS;
      return readPresets(storage);
    },
    set(presets) {
      if (storage === null) return;
      writePresets(storage, presets);
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(WIDTH_PRESETS_KEY);
      } catch {
        // ignore
      }
    },
  };
};
